import type { CategoryContent } from './row.js';

export const ANATOMY: CategoryContent = {
  bscba: [
    [
      'How many main types of bones are classified in human anatomy based on their shape?',
      [
        'Four: long, short, flat, and irregular',
        'Three: long, short, and flat',
        'Five: long, short, flat, irregular, and sesamoid',
        'Six: long, short, flat, irregular, pneumatic, and dense',
      ],
      0,
      'Bones are classified into four types by shape: long (femur), short (carpals), flat (sternum), and irregular (vertebrae).',
    ],
    [
      'Which of the following best describes the structure of a synovial joint?',
      [
        'Cartilage-covered bone ends in a fluid-filled capsule',
        'Bones held together by fibrous connective tissue with no space between them',
        'Cartilaginous disc completely fusing two bone ends',
        'Two bones connected by elastic ligaments forming a semi-mobile joint',
      ],
      0,
      'Synovial joints feature hyaline cartilage on articular surfaces, a synovial membrane lining the capsule, and synovial fluid for lubrication and nutrition.',
    ],
    [
      'What is the primary structural difference between Type I and Type II muscle fibers?',
      [
        'Type I is slow-twitch and oxidative; Type II is fast-twitch and glycolytic',
        'Type I fibers are exclusively found in the heart; Type II in skeletal muscle',
        'Type II fibers contain more mitochondria and perform aerobic metabolism',
        'Type I fibers have thicker myofilaments and generate greater force',
      ],
      0,
      'Type I fibers excel at sustained, low-force activity via oxidative metabolism. Type II fibers specialize in rapid, forceful contraction via glycolysis.',
    ],
    [
      'The right atrium receives blood from which two major vessels?',
      [
        'Superior and inferior vena cava',
        'Left and right pulmonary arteries',
        'Left and right pulmonary veins',
        'Aorta and coronary sinus',
      ],
      0,
      'The superior vena cava drains the head, neck, and upper limbs; the inferior vena cava drains everything below the diaphragm.',
    ],
    [
      'Which chamber of the heart has the thickest myocardial wall?',
      [
        'Left ventricle',
        'Right ventricle',
        'Left atrium',
        'Right atrium',
      ],
      0,
      'The left ventricle pumps systemic blood against high resistance, requiring thick muscular walls (8–15 mm). The right ventricle is thinner (2–5 mm).',
    ],
    [
      'What is the name of the tube through which blood travels from the heart to the body?',
      [
        'Aorta',
        'Carotid artery',
        'Pulmonary artery',
        'Subclavian artery',
      ],
      0,
      'The aorta is the largest artery, emerging from the left ventricle and branching to supply all systemic tissues.',
    ],
    [
      'The trachea divides into two primary bronchi at which anatomical landmark?',
      [
        'The carina',
        'The glottis',
        'The hilum',
        'The sternal angle',
      ],
      0,
      'The carina is the ridge where the trachea bifurcates into left and right main bronchi at the level of T4-T5 vertebrae.',
    ],
    [
      'How many lobes does the right lung have?',
      [
        'Three',
        'Two',
        'Four',
        'Five',
      ],
      0,
      'The right lung has three lobes (superior, middle, inferior) separated by oblique and horizontal fissures. The left has only two lobes.',
    ],
    [
      'Which structure prevents food from entering the trachea during swallowing?',
      [
        'Epiglottis',
        'Soft palate',
        'Arytenoid cartilage',
        'Vocal cords',
      ],
      0,
      'The epiglottis is a leaf-shaped cartilage that folds back over the laryngeal inlet during swallowing, protecting the airway.',
    ],
    [
      'The esophagus is a muscular tube lined with which type of epithelium?',
      [
        'Stratified squamous epithelium',
        'Simple columnar epithelium',
        'Transitional epithelium',
        'Pseudostratified ciliated epithelium',
      ],
      0,
      'Stratified squamous epithelium resists mechanical abrasion from food passage and lacks the glands and folds of the stomach.',
    ],
    [
      'What is the primary function of the fundus of the stomach?',
      [
        'Gastric juice secretion',
        'Storage of food and initial mixing',
        'Pyloric sphincter control',
        'Absorption of nutrients',
      ],
      0,
      'The fundus contains gastric glands (parietal, chief, mucous) that secrete acid, pepsin, and protective mucus. The body/fundus together store and begin mixing food.',
    ],
    [
      'The liver is primarily attached to which structure?',
      [
        'Diaphragm via the falciform ligament',
        'Stomach by the gastrohepatic ligament',
        'Spleen by the gastrosplenic ligament',
        'Pancreas by pancreaticoduodenal veins',
      ],
      0,
      'The falciform ligament (ventral mesentery) and coronary ligaments attach the liver to the diaphragm and anterior abdominal wall.',
    ],
    [
      'The kidney\'s functional filtration units are called which of the following?',
      [
        'Nephrons',
        'Glomeruli',
        'Tubules',
        'Pyramids',
      ],
      0,
      'Each nephron consists of a renal corpuscle (glomerulus + Bowman\'s capsule) and renal tubule. The human kidney contains approximately 1 million nephrons.',
    ],
    [
      'Which region of the kidney contains the glomeruli and proximal convoluted tubules?',
      [
        'Renal cortex',
        'Renal medulla',
        'Renal pelvis',
        'Renal column',
      ],
      0,
      'The renal cortex contains all glomeruli and the proximal/distal convoluted tubules. The medulla contains the loop of Henle and collecting ducts.',
    ],
    [
      'What is the anatomical term for the region where a muscle attaches to a bone that moves?',
      [
        'Insertion',
        'Origin',
        'Belly',
        'Fascia',
      ],
      0,
      'The insertion is the attachment to the moving bone (usually distal). The origin is the fixed attachment (usually proximal). Both are called entheses.',
    ],
  ],
  msc: [
    [
      'In the microstructure of compact bone, what is the Haversian system composed of?',
      [
        'Concentric lamellae around a canal with vessels and nerves',
        'Radiating trabeculae of spongy bone supported by a dense outer shell',
        'Longitudinal rows of osteocytes embedded in parallel collagen fibers',
        'Interconnected cavities lined by osteoblasts and separated by mineralized septa',
      ],
      0,
      'A Haversian (osteon) system is the smallest structural unit: concentric rings of bone matrix (lamellae) surrounding an osteonic canal with neurovascular tissue.',
    ],
    [
      'The synovial membrane of joints is derived embryologically from which germ layer?',
      [
        'Lateral plate mesoderm',
        'Paraxial mesoderm',
        'Ectoderm',
        'Endoderm',
      ],
      0,
      'The synovial membrane and joint capsule develop from lateral plate (somatic) mesoderm surrounding the developing cartilage model.',
    ],
    [
      'Which type of collagen comprises the majority of the fibrocartilage at tendon-bone entheses?',
      [
        'Type I collagen',
        'Type II collagen',
        'Type III collagen',
        'Type IV collagen',
      ],
      0,
      'Type I collagen dominates the enthesis fibrocartilage, organized in graded zones (tendon, uncalcified cartilage, calcified cartilage, bone).',
    ],
    [
      'In muscle physiology, what is the structural relationship between the Z-disc and thin filaments?',
      [
        'Thin filaments anchor to Z-discs at both ends, overlapping midway',
        'Thin filaments extend from Z-discs toward myosin heads in the A-band',
        'Z-discs are composed entirely of actin and tropomyosin molecules',
        'Thin filaments are attached perpendicular to Z-discs via titin molecules',
      ],
      0,
      'Thin filaments anchor at Z-discs via α-actinin and CapZ proteins, extending toward the M-line. This arrangement defines the sarcomere length.',
    ],
    [
      'The coronary circulation is primarily supplied by branches of which vessels arising from the aorta?',
      [
        'L/R coronary arteries',
        'Intercostal arteries',
        'Internal mammary arteries',
        'Bronchial arteries',
      ],
      0,
      'The left and right coronary arteries originate from sinuses of Valsalva (aortic cusps) and branch to supply the myocardium and epicardium.',
    ],
    [
      'Which anatomical structure is responsible for slowing conduction velocity between atria and ventricles?',
      [
        'AV node',
        'Sinoatrial node',
        'Bundle of His',
        'Purkinje fibers',
      ],
      0,
      'The AV node has slow conduction velocity (~0.05 m/s) due to small cell diameter and sparse gap junctions, allowing atrial contraction to finish before ventricular excitation.',
    ],
    [
      'The hepatic portal vein carries blood from which organ directly to the liver?',
      [
        'The GI tract and spleen',
        'Heart and lungs',
        'Kidneys and adrenal glands',
        'Brain and spinal cord',
      ],
      0,
      'The portal vein drains the GI tract, delivering nutrient-rich, drug-laden blood to hepatocytes for first-pass metabolism before systemic distribution.',
    ],
    [
      'During inspiration, contraction of the diaphragm results in which change to intrapulmonary pressure?',
      [
        'Pressure becomes negative (subatmospheric)',
        'Pressure increases above atmospheric',
        'Pressure remains constant',
        'Pressure equalizes with atmospheric pressure',
      ],
      0,
      'Diaphragm contraction increases thoracic volume, making intrapulmonary pressure negative. Air flows in following the pressure gradient.',
    ],
    [
      'The pulmonary arteries are unique among arteries in that they carry which type of blood?',
      [
        'Deoxygenated blood',
        'Oxygenated blood',
        'Mixed arterial and venous blood',
        'Lymphatic fluid',
      ],
      0,
      'Pulmonary arteries are the only systemic arteries carrying deoxygenated blood from the right ventricle to the lungs for gas exchange.',
    ],
    [
      'The gastroesophageal junction is normally maintained in a closed state by which structure?',
      [
        'The LES',
        'Upper esophageal sphincter',
        'Pyloric sphincter',
        'Ileocecal valve',
      ],
      0,
      'The LES is a zone of high-pressure smooth muscle (30–40 mmHg baseline) innervated by the vagus nerve, opening during swallowing.',
    ],
    [
      'The blood-testis barrier is created by tight junctions between which cell type?',
      [
        'Sertoli cells',
        'Leydig cells',
        'Spermatogonia',
        'Peritubular myoid cells',
      ],
      0,
      'Sertoli cell tight junctions form the blood-testis barrier, separating basal spermatogonia (exposed to systemic substances) from meiotic germ cells.',
    ],
    [
      'Which embryological germ layer gives rise to the urogenital system?',
      [
        'Intermediate mesoderm',
        'Lateral plate mesoderm',
        'Paraxial mesoderm',
        'Endoderm',
      ],
      0,
      'Intermediate mesoderm (located between paraxial and lateral plate mesoderm) gives rise to kidneys, ureters, gonads, and genital ducts.',
    ],
    [
      'The ductus arteriosus is a fetal shunt connecting which two vessels?',
      [
        'Pulmonary artery to the aorta',
        'Superior vena cava to the inferior vena cava',
        'Umbilical vein to the portal vein',
        'Left atrium to the right atrium',
      ],
      0,
      'The ductus arteriosus bypasses non-functional fetal lungs by shunting blood from the pulmonary artery to the aortic arch. It closes after birth.',
    ],
    [
      'Perimysium is the connective tissue layer that surrounds which anatomical structure?',
      [
        'Fascicles of muscle fibers',
        'Individual muscle fibers',
        'The entire skeletal muscle',
        'Myofilaments within the sarcomere',
      ],
      0,
      'Perimysium envelops muscle fascicles, maintaining their organization. Endomysium surrounds individual fibers; epimysium surrounds the whole muscle.',
    ],
    [
      'The portal triad found in liver histology consists of which three structures?',
      [
        'Hepatic artery, portal vein, and bile duct',
        'Hepatic vein, hepatic artery, and hepatic duct',
        'Sinusoid, hepatocyte, and Kupffer cell',
        'Centrilobular vein, stellate cell, and bile canaliculus',
      ],
      0,
      'Each portal triad (at the corners of liver lobules) contains a branch of the hepatic artery, portal vein, and bile duct running in parallel.',
    ],
  ],
  phd: [
    [
      'Bone remodeling is coordinated by which molecular signaling pathway activated by mechanical strain?',
      [
        'Wnt/β-catenin signaling via mechanotransduction',
        'Notch signaling cascade in osteoblast differentiation',
        'Hedgehog signaling in osteoclast activation',
        'FGF receptor signaling in endochondral ossification',
      ],
      0,
      'Osteocytes sense mechanical load via integrins and ion channels, activating Wnt/β-catenin signaling to suppress sclerostin and promote bone formation.',
    ],
    [
      'The enthesis contains a unique zone of mineralized fibrocartilage with calcium hydroxyapatite crystals oriented at which angle to collagen fibrils?',
      [
        '45 degrees',
        'Parallel (0 degrees)',
        'Perpendicular (90 degrees)',
        'Random (variable angles)',
      ],
      0,
      'The calcified cartilage zone of the enthesis has apatite crystals oriented at ~45° to collagen fibrils, creating a graded mechanical transition.',
    ],
    [
      'During the powerstroke of myosin, the myosin head pivots around which functional region?',
      [
        'The neck region linking domain and lever arm',
        'The ATP-binding pocket in the nucleotide-binding cleft',
        'The actin-binding interface on the lower 50 kDa domain',
        'The regulatory light chain at the end of the lever arm',
      ],
      0,
      'Myosin\'s neck region (converter domain) acts as a lever arm hinge. Conformational changes during ATP hydrolysis amplify small atomic movements into large displacement.',
    ],
    [
      'The coronary circulation exhibits metabolic autoregulation through which primary mechanism?',
      [
        'Adenosine buildup lowers arteriolar resistance',
        'Myogenic response to changes in perfusion pressure',
        'Sympathetic nervous system vasoconstriction',
        'Endothelial nitric oxide production from shear stress',
      ],
      0,
      'Increased cardiac metabolism depletes ATP, generating adenosine that activates A2A receptors on vascular smooth muscle, causing vasodilation.',
    ],
    [
      'Fetal shunting via the foramen ovale is maintained by which pressure gradient mechanism?',
      [
        'Left atrial pressure exceeds right, sealing the septum primum',
        'Right atrial pressure exceeds left atrial pressure, forcing blood across the defect',
        'The ductus venosus diverts blood directly into the left atrium',
        'Preferential pulmonary vascular dilation increases left-to-right shunting',
      ],
      0,
      'The foramen ovale acts as a one-way valve: when left atrial pressure rises above right (after birth), the septum primum seals the opening.',
    ],
    [
      'Accessory muscles of respiration, including the scalenes and sternocleidomastoid, attach to which ribs at their distal insertion?',
      [
        'First and second ribs',
        'Fifth and sixth ribs',
        'Eighth through twelfth ribs',
        'All ribs equally from first to twelfth',
      ],
      0,
      'Scalenes attach to ribs 1–2; sternocleidomastoid aids with rib 1. These muscles elevate the chest wall during forced inspiration.',
    ],
    [
      'The blood-air barrier of the alveolus consists of how many cell layers total?',
      [
        'Three: epithelium, basement membrane, endothelium',
        'Two: alveolar epithelium and capillary endothelium only',
        'Four: including a distinct interstitial layer',
        'Five: with an additional surfactant-producing mucous layer',
      ],
      0,
      'The barrier is ~0.5 μm thick: alveolar epithelium (type I cells), fused basement membranes, and capillary endothelium. Thickness minimizes diffusion distance.',
    ],
    [
      'In the ventilation-perfusion (V/Q) matching of lung zones, which anatomical factor creates regional differences in blood flow?',
      [
        'Gravity creates a pressure gradient down the lung',
        'Differential branching of the pulmonary arteries by lobe',
        'Preferential bronchiolar dilation in apical segments',
        'Unequal distribution of surfactant across lung regions',
      ],
      0,
      'Gravity creates a vertical pressure gradient: apical zones (low perfusion) have high V/Q; basal zones (high perfusion) have low V/Q.',
    ],
    [
      'The intestinal epithelium is renewed every 3–5 days through proliferation of stem cells in which crypt structure?',
      [
        'Crypts of Lieberkühn',
        'Brunner\'s glands',
        'Peyer\'s patches',
        'Plicae circulares',
      ],
      0,
      'Stem cells in crypt bases divide, generating enterocytes that migrate up villi and are shed at the apex, maintaining constant epithelial turnover.',
    ],
    [
      'The sphincter of Oddi controls flow between the ampulla of Vater and which organ?',
      [
        'Duodenum, via the major papilla',
        'Jejunum via the ligament of Treitz',
        'Stomach via the pyloric sphincter',
        'Ileum via the ileocecal valve',
      ],
      0,
      'The sphincter of Oddi regulates bile and pancreatic juice flow into the duodenum. Cholecystokinin causes relaxation during digestion.',
    ],
    [
      'The juxtaglomerular apparatus senses changes in plasma osmolality and sodium concentration, triggering renin release when which condition is detected?',
      [
        'Low renal perfusion or low sodium at the macula densa',
        'Increased filtration pressure in Bowman\'s capsule',
        'Elevation of glomerular filtration rate above baseline',
        'Hyperkalemia detected by granular cells',
      ],
      0,
      'Baroreceptors in afferent arterioles sense hypotension; chemoreceptors (macula densa) sense low NaCl. Both trigger renin release.',
    ],
    [
      'The Müllerian (paramesonephric) duct gives rise to which reproductive structures in females?',
      [
        'Fallopian tubes and uterus',
        'Ovaries and follicle cells',
        'Vagina and vestibular glands only',
        'Clitoris and greater vestibular glands',
      ],
      0,
      'The Müllerian ducts fuse caudally to form the uterus and vagina; cranially they form fallopian tubes. Suppression by anti-Müllerian hormone occurs in males.',
    ],
    [
      'During testicular descent, the gubernaculum attaches the testis to which structure guiding migration?',
      [
        'The scrotum, via a condensation',
        'The epididymis via the mesorchium',
        'The internal spermatic fascia directly',
        'The inguinal ligament via cremaster muscle',
      ],
      0,
      'The gubernaculum is a mesenchymal condensation anchoring the testis to the labioscrotal swellings, guiding transinguinal descent via differential growth.',
    ],
    [
      'The diaphragm develops from which four embryological sources?',
      [
        'Septum transversum, pleuroperitoneal folds, esophageal mesentery, body wall',
        'Lateral plate mesoderm, intermediate mesoderm, somatic mesoderm, and endoderm',
        'Paraxial mesoderm alone, segmented into phrenic somites',
        'Neural crest cells migrating ventrally from the brainstem',
      ],
      0,
      'The diaphragm\'s central tendon (septum transversum), crura (dorsal esophageal mesentery), and peripheral muscularization (body wall) form by ~week 8 gestation.',
    ],
    [
      'In the female urethra, the paraurethral glands (Skene\'s glands) are embryological homologs of which male structure?',
      [
        'The prostate gland',
        'The bulbourethral (Cowper\'s) glands',
        'The urethral glands of Littré',
        'The seminal vesicles',
      ],
      0,
      'Skene\'s glands develop from urethral endoderm similar to prostate tissue, making them prostatic homologs. They secrete a weakly acidic fluid.',
    ],
  ],
  professor: [
    [
      'Mechanical loading induces osteocyte dendrites to transmit signals via which extracellular mechanism that eventually suppresses osteoclast activation?',
      [
        'Canalicular fluid shear activates connexin-43, lowering sclerostin',
        'Direct contact between osteocyte processes and osteoclast precursors via RANKL downregulation',
        'Increased interstitial fluid pressure triggering osteoprotegerin release from bone lining cells',
        'Mechanical distortion of integrin-rich focal adhesions releasing sequestered BMP-6',
      ],
      0,
      'Osteocyte dendrites sense fluid flow through lacunocanalicular networks. This activates Wnt signaling, decreasing sclerostin and increasing OPG/RANKL ratio.',
    ],
    [
      'The ultrastructural geometry of the calcified fibrocartilage enthesis involves apatite crystals organized in a preferred orientation relative to load vectors. What is the biological consequence of this anisotropy?',
      [
        'Heterogeneous stress dissipates energy at the interface',
        'Uniform mineralization across the zone prevents microcrack propagation into adjacent bone',
        'Oriented crystals increase water uptake, improving viscoelastic damping',
        'Crystal anisotropy allows rapid adaptive remodeling in response to changing load direction',
      ],
      0,
      'Angled apatite crystals (45° to fibrils) create graded stiffness, dispersing stress heterogeneously and preventing catastrophic failure at the tendon-bone interface.',
    ],
    [
      'The myosin powerstroke generates force through a conformational change where the converter domain pivots ~70° around the neck region. What thermodynamic condition makes this transition energetically favorable?',
      [
        'Phosphate release lowers the pre-powerstroke ground state',
        'ATP rebinding immediately stabilizes the post-powerstroke state, driving the transition forward',
        'Actin binding increases the activation energy of the reverse reaction, making backward steps negligible',
        'Thermal energy at body temperature equals the ~3 pN·nm of work performed per stroke',
      ],
      0,
      'Pi release drops myosin to a lower-energy state favoring the powerstroke. The ~50 kJ/mol energy from ATP hydrolysis makes the 70° pivot exergonic.',
    ],
    [
      'The fetal ductus venosus allows placental blood to bypass hepatic sinusoids. What is the embryological fate of this shunt after birth, and what genetic factor governs its closure?',
      [
        'It constricts as portal flow rises, then intimal endothelium obliterates it',
        'It remains patent indefinitely in 30% of adults as an anatomical variant',
        'It involutes via apoptosis triggered by HIF-1α in response to increased oxygen tension',
        'It is mechanically closed by umbilical vein thrombosis before hepatic circulation matures',
      ],
      0,
      'The ductus venosus closes functionally within hours of birth as portal pressure rises. Endothelial proliferation obliterates it into the ligamentum venosum by weeks.',
    ],
    [
      'In coronary autoregulation, the relationship between coronary blood flow and myocardial oxygen consumption is maintained constant across a wide perfusion pressure range. Which arteriolar mechanism primarily accounts for this metabolic coupling at physiological heart rates?',
      [
        'Adenosine from ATP catabolism activates A2A receptors, opposing vasoconstriction',
        'Myogenic mechanisms sensing transmural pressure changes via mechanoreceptor ion channels',
        'Endothelial nitric oxide production proportional to oxygen consumption rates',
        'Sympathetic nerve withdrawal reducing α-adrenergic tone during increased metabolic demand',
      ],
      0,
      'Cardiac metabolism generates adenosine as ATP is hydrolyzed. Adenosine acts on A2A receptors to vasodilate, maintaining constant flow-to-metabolic demand ratio.',
    ],
    [
      'The scalenes (anterior, medius, posterior) are innervated by which cervical nerve roots, and what is their mechanical role during forced inspiration?',
      [
        'Cervical 3–8; they elevate ribs 1–2 to widen the thoracic inlet',
        'Cervical 1–2 only; they compress the thoracic cavity for expiration',
        'Thoracic 1–4; they stabilize the mediastinum',
        'Cervical 5–7; they depress ribs 3–6 during quiet respiration',
      ],
      0,
      'Scalenes (C3–C8) insert on ribs 1–2. During forced breathing, they pull ribs cranially, enlarging the superior thoracic aperture.',
    ],
    [
      'The pulmonary circulation exhibits unique compliance characteristics compared to the systemic circulation. How does gravity-induced hydrostatic pressure affect capillary recruitment and regional V/Q matching in upright humans?',
      [
        'Apex is ventilation-limited; base is perfusion-limited',
        'Apical capillaries are maximally recruited; basal capillaries remain collapsed due to low transmural pressure',
        'Gravity has negligible effect; regional V/Q differences are determined entirely by bronchiolar tone',
        'Hydrostatic pressure increases capillary permeability uniformly, reducing diffusion distance globally',
      ],
      0,
      'Gravity creates vertical hydrostatic gradients. Apical zones have low perfusion; basal zones have high capillary transmural pressure and distension.',
    ],
    [
      'The small intestinal epithelium exhibits a proliferation-differentiation gradient with stem cells in crypts dividing every 24–48 hours. What molecular factors regulate transit amplification and apical migration of progenitor cells along this axis?',
      [
        'Wnt is high in crypts; BMP/TGF-β is high on villi, opposing it',
        'Notch signaling exclusively in transit-amplifying cells blocks further division',
        'Hedgehog ligands secreted apically repel differentiating cells basally',
        'FGF10 from mesenchymal fibroblasts drives all epithelial proliferation uniformly',
      ],
      0,
      'High Wnt in crypts promotes stemness; elevated BMP on villi promotes differentiation. This opposing gradient drives directional migration and cell fate.',
    ],
    [
      'Bile acid-dependent activation of the farnesoid X receptor (FXR) in ileal enterocytes triggers which intestinal and hepatic feedback loops regulating cholesterol homeostasis?',
      [
        'FGF19 secretion inhibits CYP7A1, cutting bile acid synthesis',
        'Direct hepatocyte activation of SREBP-2 increases cholesterol uptake and synthesis',
        'Reduced portal glucose signaling downregulates hepatic lipogenesis',
        'Ileal barrier tightening prevents secondary bile acid absorption, reducing enterohepatic circulation',
      ],
      0,
      'Ileal FXR activation triggers FGF19 secretion. FGF19 acts on hepatic FGFR4 to induce SHP and suppress CYP7A1, reducing bile acid synthesis.',
    ],
    [
      'The tubuloglomerular feedback (TGF) mechanism detects changes in sodium chloride concentration at the macula densa and modulates glomerular filtration rate through which effector mechanism?',
      [
        'NKCC2-mediated NaCl uptake releases adenosine, constricting the afferent arteriole',
        'Macula densa cells directly sense angiotensin II in the juxtaglomerular space',
        'Increased intraglomerular pressure directly opens mechanoreceptors on granular cells',
        'Prostaglandin E2 released from collecting duct cells inhibits renin secretion systemically',
      ],
      0,
      'NaCl uptake via NKCC2 increases metabolism and purinergic signaling. ATP/adenosine causes afferent arteriolar vasoconstriction, reducing GFR and renin secretion.',
    ],
    [
      'Anti-Müllerian hormone (AMH), secreted by Sertoli cells, suppresses Müllerian duct development in males via which receptor and intracellular signaling cascade?',
      [
        'Type II serine/threonine kinase receptor activating SMAD2/3-SMAD4',
        'Estrogen receptor β on Müllerian mesenchyme, blocking paracrine Wnt signaling',
        'Notch signaling in mesonephric mesenchyme triggering caspase-mediated Müllerian duct apoptosis',
        'TrkB tyrosine kinase receptor in ductal epithelium activating FoxA2-dependent differentiation into Wolffian structures',
      ],
      0,
      'AMH binds type II serine/threonine kinase receptors (AMHR2), recruiting type I receptors (ALK2/3). SMAD2/3-SMAD4 activation blocks Müllerian fate genes.',
    ],
    [
      'The gubernacular mesenchyme of the fetal testis expresses which growth factors and transcription factors that promote descent, and how do they interact with the surrounding genital ridge?',
      [
        'INSL3 from Leydig cells activates RXFP2, upregulating desmin for migration',
        'BMP4 from coelomic epithelium drives gubernacular ossification',
        'Sonic hedgehog from mesonephric mesenchyme triggers apoptosis-driven gubernacular resorption',
        'GDNF from the ureteric bud attracts testicular germ cells via RET signaling',
      ],
      0,
      'INSL3-RXFP2 signaling in gubernacular cells upregulates desmin (myofibroblast marker) and migration genes. This is essential for testicular descent.',
    ],
    [
      'The diaphragm\'s muscularization involves ingrowth of phrenic (C3–C5) and lower intercostal (T7–T12) motor nerve axons. What embryological process explains the dual innervation of this structure that develops from multiple germ origins?',
      [
        'Phrenic nerve to the central tendon; intercostal nerves to the body-wall muscle',
        'The diaphragm develops solely from paraxial mesoderm, and dual innervation reflects plurisegmental myotome origin',
        'Phrenic and intercostal nerves undergo extensive commissural connections post-natally',
        'Lower cervical and thoracic motor pools undergo Hox gene-dependent migration toward the developing diaphragm',
      ],
      0,
      'Dual innervation reflects developmental origins: phrenic nerve innervates the central tendon (mesoderm near the heart); intercostal nerves innervate the muscular periphery (body wall).',
    ],
    [
      'The female orgasm involves coordinated rhythmic contractions (0.8 Hz) of the uterus, vagina, and pelvic floor muscles. What is the physiological basis for the increased genital blood flow and myotonia preceding orgasm, and which neurotransmitter system is responsible for the refractory period afterward?',
      [
        'NO/VIP vasocongestion and adrenergic myotonia; norepinephrine drives the refractory period',
        'Estrogen upregulates α-adrenergic receptors, causing vasoconstriction and myotonia that is reversed by oxytocin during orgasm',
        'Parasympathetic-driven parasympathetic vasodilation followed by sympathetic inhibition during refractory period',
        'Pelvic splanchnic nerve stimulation directly triggers rhythmic uterine contractions independent of hormonal factors',
      ],
      0,
      'NO/VIP cause vasocongestion and blood flow; sympathetic tone produces myotonia. Post-orgasm, elevated norepinephrine causes adrenergic-mediated inhibition of continued response.',
    ],
    [
      'In the developing cardiovascular system, endothelial cells lining the cardiac outflow tract undergo epithelial-mesenchymal transition (EMT) to form neural crest-derived smooth muscle. Which transcription factor network governs this invasion of the endocardial cushions?',
      [
        'Snail/Slug repress E-cadherin, aided by Wnt/β-catenin and Notch',
        'Direct MyoD-dependent myogenic differentiation of endothelial cells into cardiomyocytes',
        'HIF-1α-dependent metabolic shift from oxidative phosphorylation to glycolysis',
        'Krüppel-like factor 4 (KLF4) reactivation of a pluripotent gene expression program',
      ],
      0,
      'Snail/Slug repress E-cadherin, allowing endothelial migration. Wnt/β-catenin and Notch together drive mesenchymal differentiation in cushion tissue formation.',
    ],
  ],
};
